import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { checkBusinessNameAvailability } from '../services/businessNameValidation';
import { checkSubdomainAvailability } from '../services/dnsValidation';
import { ENDPOINTS } from '../config/endpoints';
import { AvailabilityStatus } from '../types';

interface SessionData {
  token: string;
  networkId: string;
  networkName: string;
  expiresAt: string;
  networkConfig: {
    requires_fca: boolean;
    allow_bulk_upload: boolean;
    max_bulk_upload: number;
    branding: {
      primary_color: string;
      logo_url?: string;
    };
  };
}

interface FirmData {
  businessName: string;
  fcaNumber: string;
  ceoFirstName: string;
  ceoLastName: string;
  ceoEmail: string;
  dnsSubdomain: string;
}

interface FormErrors {
  [key: string]: string;
}

export const NetworkOnboardingForm: React.FC = () => {
  const { networkId } = useParams<{ networkId: string }>();
  const navigate = useNavigate();

  const [sessionData, setSessionData] = useState<SessionData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string>('');

  const [formData, setFormData] = useState<FirmData>({
    businessName: '',
    fcaNumber: '',
    ceoFirstName: '',
    ceoLastName: '',
    ceoEmail: '',
    dnsSubdomain: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [businessNameChecking, setBusinessNameChecking] = useState<boolean>(false);
  const [businessNameAvailable, setBusinessNameAvailable] = useState<boolean | null>(null);
  // 'check_failed' = we could not reach the registry. Distinct from 'taken':
  // reporting one as the other tells a firm their free name is registered.
  const [businessNameStatus, setBusinessNameStatus] = useState<AvailabilityStatus | null>(null);
  const [businessNameMessage, setBusinessNameMessage] = useState<string>('');
  const [subdomainChecking, setSubdomainChecking] = useState<boolean>(false);
  const [subdomainAvailable, setSubdomainAvailable] = useState<boolean | null>(null);
  const [subdomainStatus, setSubdomainStatus] = useState<AvailabilityStatus | null>(null);
  const [subdomainMessage, setSubdomainMessage] = useState<string>('');

  // Check for valid session on mount
  useEffect(() => {
    const storedSession = localStorage.getItem('admin_session');
    if (!storedSession) {
      navigate('/admin');
      return;
    }

    try {
      const session: SessionData = JSON.parse(storedSession);
      const expiresAt = new Date(session.expiresAt);

      // Check if session is still valid
      if (expiresAt <= new Date()) {
        localStorage.removeItem('admin_session');
        navigate('/admin');
        return;
      }

      // Check if network ID matches
      if (session.networkId !== networkId) {
        navigate('/admin');
        return;
      }

      setSessionData(session);
    } catch (e) {
      localStorage.removeItem('admin_session');
      navigate('/admin');
    }
  }, [networkId, navigate]);

  const handleChange = (field: keyof FirmData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    setSubmitError('');

    // Check business name availability when business name changes
    if (field === 'businessName' && value.trim().length >= 2) {
      setBusinessNameAvailable(null);
      setBusinessNameStatus(null);
      setBusinessNameMessage('');
      setBusinessNameChecking(true);

      const timeoutId = setTimeout(async () => {
        try {
          const result = await checkBusinessNameAvailability(value.trim());
          setBusinessNameAvailable(result.available);
          setBusinessNameStatus(result.status ?? (result.available ? 'available' : 'taken'));
          setBusinessNameMessage(result.message || '');
        } catch (error) {
          console.error('Business name check error:', error);
          setBusinessNameAvailable(false);
          setBusinessNameStatus('check_failed');
          setBusinessNameMessage("We couldn't check this name right now. Please try again.");
        } finally {
          setBusinessNameChecking(false);
        }
      }, 800);

      return () => clearTimeout(timeoutId);
    } else if (field === 'businessName') {
      // Reset if business name is too short
      setBusinessNameAvailable(null);
      setBusinessNameStatus(null);
      setBusinessNameMessage('');
    }
  };

  const handleSubdomainChange = (value: string) => {
    const cleanValue = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    handleChange('dnsSubdomain', cleanValue);

    // Reset subdomain status when user types
    setSubdomainAvailable(null);
    setSubdomainStatus(null);
    setSubdomainMessage('');

    // Check subdomain availability after user stops typing (debounced)
    if (cleanValue.length >= 3) {
      setSubdomainChecking(true);
      const timeoutId = setTimeout(async () => {
        try {
          const result = await checkSubdomainAvailability(cleanValue);
          setSubdomainAvailable(result.available);
          setSubdomainStatus(result.status ?? (result.available ? 'available' : 'taken'));
          setSubdomainMessage(result.message || '');
        } catch (error) {
          console.error('Subdomain check error:', error);
          setSubdomainAvailable(false);
          setSubdomainStatus('check_failed');
          setSubdomainMessage("We couldn't check this address right now. Please try again.");
        } finally {
          setSubdomainChecking(false);
        }
      }, 800);

      return () => clearTimeout(timeoutId);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.businessName.trim()) {
      newErrors.businessName = 'Business name is required';
    } else if (businessNameAvailable === false && businessNameStatus !== 'check_failed') {
      newErrors.businessName = 'This business name is already registered';
    } else if (businessNameChecking) {
      newErrors.businessName = 'Checking business name availability...';
    } else if (businessNameAvailable === null && formData.businessName.trim().length >= 2) {
      newErrors.businessName = 'Please wait while we check business name availability';
    }

    if (sessionData?.networkConfig.requires_fca && !formData.fcaNumber.trim()) {
      newErrors.fcaNumber = 'FCA number is required';
    }

    if (!formData.ceoFirstName.trim()) {
      newErrors.ceoFirstName = 'CEO first name is required';
    }

    if (!formData.ceoLastName.trim()) {
      newErrors.ceoLastName = 'CEO last name is required';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.ceoEmail.trim()) {
      newErrors.ceoEmail = 'CEO email is required';
    } else if (!emailRegex.test(formData.ceoEmail)) {
      newErrors.ceoEmail = 'Invalid email format';
    }

    if (!formData.dnsSubdomain.trim()) {
      newErrors.dnsSubdomain = 'Subdomain is required';
    } else if (formData.dnsSubdomain.length < 3) {
      newErrors.dnsSubdomain = 'Subdomain must be at least 3 characters';
    } else if (subdomainAvailable === false && subdomainStatus !== 'check_failed') {
      newErrors.dnsSubdomain = 'This subdomain is already taken';
    } else if (subdomainChecking) {
      newErrors.dnsSubdomain = 'Checking subdomain availability...';
    } else if (subdomainAvailable === null && formData.dnsSubdomain.length >= 3) {
      newErrors.dnsSubdomain = 'Please wait while we check subdomain availability';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!sessionData) {
      setSubmitError('Session expired. Please sign in again.');
      navigate('/admin');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const response = await fetch(ENDPOINTS.NETWORK_ONBOARDING, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionToken: sessionData.token,
          networkId: sessionData.networkId,
          firms: [formData],
          // Always deploy to production environment
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitSuccess(true);

        // Reset form after 3 seconds
        setTimeout(() => {
          setFormData({
            businessName: '',
            fcaNumber: '',
            ceoFirstName: '',
            ceoLastName: '',
            ceoEmail: '',
            dnsSubdomain: '',
          });
          setSubmitSuccess(false);
        }, 3000);
      } else {
        setSubmitError(data.error || 'Failed to submit onboarding request');
      }
    } catch (err) {
      console.error('Submission error:', err);
      setSubmitError('Unable to submit request. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_session');
    navigate('/admin');
  };

  if (!sessionData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const primaryColor = sessionData.networkConfig.branding.primary_color;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {sessionData.networkConfig.branding.logo_url && (
                <img
                  src={sessionData.networkConfig.branding.logo_url}
                  alt={sessionData.networkName}
                  className="h-8 w-auto"
                />
              )}
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  {sessionData.networkName} - Admin Portal
                </h1>
                <p className="text-sm text-gray-600">
                  Onboard member firms to Smartr365
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Single Firm Form */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Onboard New Firm
              </h2>
              <p className="text-gray-600">
                Enter the details for a member firm to onboard them to Smartr365
              </p>
            </div>

            {/* Success Message */}
            {submitSuccess && (
              <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-green-600 font-medium">
                    Firm onboarded successfully! Pipeline has been triggered.
                  </p>
                </div>
              </div>
            )}

            {/* Error Message */}
            {submitError && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm text-red-600">{submitError}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Business Name */}
              <div>
                <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-2">
                  Business Name *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="businessName"
                    value={formData.businessName}
                    onChange={(e) => handleChange('businessName', e.target.value)}
                    className={classNames(
                      'w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2',
                      {
                        'border-red-300 focus:border-red-500 focus:ring-red-500': errors.businessName || businessNameStatus === 'taken',
                        'border-amber-300 focus:border-amber-500 focus:ring-amber-500': !errors.businessName && businessNameStatus === 'check_failed',
                        'border-green-300 focus:border-green-500 focus:ring-green-500': businessNameAvailable === true,
                        'border-gray-300 focus:ring-blue-500': !errors.businessName && businessNameAvailable === null,
                      }
                    )}
                    placeholder="Enter business name"
                    disabled={isSubmitting}
                  />
                  {businessNameChecking && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  )}
                  {!businessNameChecking && businessNameAvailable === true && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  {!businessNameChecking && businessNameStatus === 'taken' && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
                {errors.businessName && (
                  <p className="mt-1 text-sm text-red-600">{errors.businessName}</p>
                )}
                {businessNameAvailable === true && !errors.businessName && (
                  <p className="mt-1 text-sm text-green-600">✓ {businessNameMessage}</p>
                )}
                {businessNameStatus === 'taken' && !errors.businessName && (
                  <p className="mt-1 text-sm text-red-600">✗ {businessNameMessage}</p>
                )}
                {businessNameStatus === 'check_failed' && !errors.businessName && (
                  <p className="mt-1 text-sm text-amber-700">
                    {businessNameMessage} You can continue - we'll confirm the name when the account is set up.
                  </p>
                )}
              </div>

              {/* FCA Number */}
              {sessionData.networkConfig.requires_fca && (
                <div>
                  <label htmlFor="fcaNumber" className="block text-sm font-medium text-gray-700 mb-2">
                    FCA Number *
                  </label>
                  <input
                    type="text"
                    id="fcaNumber"
                    value={formData.fcaNumber}
                    onChange={(e) => handleChange('fcaNumber', e.target.value)}
                    className={classNames(
                      'w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2',
                      {
                        'border-red-300 focus:border-red-500 focus:ring-red-500': errors.fcaNumber,
                        'border-gray-300 focus:ring-blue-500': !errors.fcaNumber,
                      }
                    )}
                    placeholder="Enter FCA number"
                    disabled={isSubmitting}
                  />
                  {errors.fcaNumber && (
                    <p className="mt-1 text-sm text-red-600">{errors.fcaNumber}</p>
                  )}
                </div>
              )}

              {/* CEO First Name */}
              <div>
                <label htmlFor="ceoFirstName" className="block text-sm font-medium text-gray-700 mb-2">
                  CEO First Name *
                </label>
                <input
                  type="text"
                  id="ceoFirstName"
                  value={formData.ceoFirstName}
                  onChange={(e) => handleChange('ceoFirstName', e.target.value)}
                  className={classNames(
                    'w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2',
                    {
                      'border-red-300 focus:border-red-500 focus:ring-red-500': errors.ceoFirstName,
                      'border-gray-300 focus:ring-blue-500': !errors.ceoFirstName,
                    }
                  )}
                  placeholder="Enter CEO first name"
                  disabled={isSubmitting}
                />
                {errors.ceoFirstName && (
                  <p className="mt-1 text-sm text-red-600">{errors.ceoFirstName}</p>
                )}
              </div>

              {/* CEO Last Name */}
              <div>
                <label htmlFor="ceoLastName" className="block text-sm font-medium text-gray-700 mb-2">
                  CEO Last Name *
                </label>
                <input
                  type="text"
                  id="ceoLastName"
                  value={formData.ceoLastName}
                  onChange={(e) => handleChange('ceoLastName', e.target.value)}
                  className={classNames(
                    'w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2',
                    {
                      'border-red-300 focus:border-red-500 focus:ring-red-500': errors.ceoLastName,
                      'border-gray-300 focus:ring-blue-500': !errors.ceoLastName,
                    }
                  )}
                  placeholder="Enter CEO last name"
                  disabled={isSubmitting}
                />
                {errors.ceoLastName && (
                  <p className="mt-1 text-sm text-red-600">{errors.ceoLastName}</p>
                )}
              </div>

              {/* CEO Email */}
              <div>
                <label htmlFor="ceoEmail" className="block text-sm font-medium text-gray-700 mb-2">
                  CEO Email *
                </label>
                <input
                  type="email"
                  id="ceoEmail"
                  value={formData.ceoEmail}
                  onChange={(e) => handleChange('ceoEmail', e.target.value)}
                  className={classNames(
                    'w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2',
                    {
                      'border-red-300 focus:border-red-500 focus:ring-red-500': errors.ceoEmail,
                      'border-gray-300 focus:ring-blue-500': !errors.ceoEmail,
                    }
                  )}
                  placeholder="Enter CEO email"
                  disabled={isSubmitting}
                />
                {errors.ceoEmail && (
                  <p className="mt-1 text-sm text-red-600">{errors.ceoEmail}</p>
                )}
              </div>

              {/* DNS Subdomain */}
              <div>
                <label htmlFor="dnsSubdomain" className="block text-sm font-medium text-gray-700 mb-2">
                  Platform Subdomain *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="dnsSubdomain"
                    value={formData.dnsSubdomain}
                    onChange={(e) => handleSubdomainChange(e.target.value)}
                    className={classNames(
                      'w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 font-mono',
                      {
                        'border-red-300 focus:border-red-500 focus:ring-red-500': errors.dnsSubdomain || subdomainStatus === 'taken',
                        'border-amber-300 focus:border-amber-500 focus:ring-amber-500': !errors.dnsSubdomain && subdomainStatus === 'check_failed',
                        'border-green-300 focus:border-green-500 focus:ring-green-500': subdomainAvailable === true,
                        'border-gray-300 focus:ring-blue-500': !errors.dnsSubdomain && subdomainAvailable === null,
                      }
                    )}
                    placeholder="Enter subdomain (e.g., example)"
                    disabled={isSubmitting}
                  />
                  {subdomainChecking && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  )}
                  {!subdomainChecking && subdomainAvailable === true && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                  {!subdomainChecking && subdomainStatus === 'taken' && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
                {errors.dnsSubdomain && (
                  <p className="mt-1 text-sm text-red-600">{errors.dnsSubdomain}</p>
                )}
                {subdomainMessage && !errors.dnsSubdomain && (
                  <p className={classNames('mt-1 text-sm', {
                    'text-green-600': subdomainAvailable === true,
                    'text-red-600': subdomainStatus === 'taken',
                    'text-amber-700': subdomainStatus === 'check_failed',
                  })}>
                    {subdomainMessage}
                    {subdomainStatus === 'check_failed' &&
                      " You can continue - we'll confirm this address when the account is set up."}
                  </p>
                )}
                {formData.dnsSubdomain && !errors.dnsSubdomain && subdomainAvailable === true && (
                  <p className="mt-2 text-sm text-gray-600">
                    Platform URL: <span className="font-mono font-medium">{formData.dnsSubdomain}.smartr365.co.uk</span>
                  </p>
                )}
              </div>

              {/* Network Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <svg className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-blue-800">Network: {sessionData.networkName}</p>
                    <p className="text-sm text-blue-700 mt-1">
                      This firm will be onboarded as part of your network with pre-configured settings.
                    </p>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                style={{ backgroundColor: isSubmitting ? undefined : primaryColor }}
                className={classNames(
                  'w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 flex items-center justify-center space-x-2',
                  {
                    'text-white shadow-lg hover:shadow-xl': !isSubmitting,
                    'bg-gray-300 text-gray-500 cursor-not-allowed': isSubmitting,
                  }
                )}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Onboard Firm</span>
                  </>
                )}
              </button>
            </form>
          </div>
      </div>
    </div>
  );
};
